import type { PortableTextProjection, StringProjection } from '@lib/sanity/types/common';
import { DEFAULT_LANG, LANGUAGES } from '../constants/languages';
import { getUrlWithTrailingSlash } from './urls';

export function getLanguageFromPathname(pathname: string) {
  const [, id] = pathname.split('/');
  const language = LANGUAGES.find((lang) => lang.id === id);

  if (language) {
    return language;
  }

  return LANGUAGES.find((lang) => lang.id === DEFAULT_LANG) ?? LANGUAGES[0];
}

export function getLanguageHref(pathname: string | undefined, newId: string, force = false, isDraftMode = false) {
  if (!pathname || pathname.startsWith('http')) {
    return pathname;
  }
  if (pathname.startsWith('javascript:') || pathname.startsWith('mailto:')) {
    return pathname;
  }

  // Skip processing static files (files with extensions)
  const hasFileExtension = /\.[^/]+$/.test(pathname);
  if (hasFileExtension) {
    return pathname;
  }

  // Validate that newId is a valid language code
  const newLanguage = LANGUAGES.find((lang) => lang.id === newId);
  if (!newLanguage) {
    // If newId is not a valid language, return the pathname as-is to avoid errors
    return pathname;
  }

  if (!pathname.startsWith('/')) {
    pathname = `/${pathname}`;
  }
  if (isDraftMode) {
    pathname = `/preview${pathname}`;
  }

  const [, id] = pathname.split('/');
  const language = LANGUAGES.find((lang) => lang.id === id);
  let res = '';
  if (newLanguage.id === DEFAULT_LANG && !force) {
    res = language ? pathname.replace(`/${id}/`, '/') : pathname;
  } else {
    res = language ? pathname.replace(`/${id}/`, `/${newId}/`) : `/${newId}${pathname}`;
  }

  return getUrlWithTrailingSlash(res);
}

type UrlObject = { url?: string };

export function updateHrefInObject<T extends UrlObject | undefined>(obj: T, lang: string, isDraftMode = false): T {
  if (!obj?.url) {
    return obj;
  }

  return {
    ...obj,
    url: getLanguageHref(obj.url, lang, false, isDraftMode),
  };
}

export function updateHrefInPortableText<
  T extends PortableTextProjection | PortableTextProjection[] | string | undefined,
>(blocks: T, lang: string, isDraftMode = false): T {
  if (!blocks) {
    return blocks;
  }

  if (typeof blocks === 'string') {
    return updateHrefInText(blocks, lang, isDraftMode) as T;
  }

  const updatePortableTextItem = (item: PortableTextProjection) => ({
    ...item,
    value:
      typeof item.value === 'string'
        ? updateHrefInStringField(item.value, lang, isDraftMode)
        : item.value?.map((block) => ({
            ...block,
            markDefs: block.markDefs?.map((markDef) => {
              if (!('_type' in markDef) || markDef._type !== 'link') {
                return markDef;
              }
              return {
                ...markDef,
                url: markDef.url ? getLanguageHref(markDef.url, lang, false, isDraftMode) : markDef.url,
              };
            }),
          })),
  });

  if (Array.isArray(blocks)) {
    return blocks.map(updatePortableTextItem) as T;
  }

  return updatePortableTextItem(blocks) as T;
}

export function updateHrefInStringField<T extends StringProjection | StringProjection[] | undefined>(
  field: T,
  lang: string,
  isDraftMode = false,
): T {
  if (Array.isArray(field)) {
    return field.map((item) => ({
      ...item,
      value: updateHrefInText(item.value, lang, isDraftMode),
    })) as T;
  }

  if (!field?.value) {
    return field;
  }

  return {
    ...field,
    value: updateHrefInText(field.value, lang, isDraftMode),
  };
}

export function updateHrefInText<T extends string | null | undefined>(text: T, lang: string, isDraftMode = false): T {
  if (!text) {
    return text;
  }

  return text.replace(/href="([^"]+)"/g, (_, href) => {
    return `href="${getLanguageHref(href, lang, false, isDraftMode)}"`;
  }) as T;
}
